import { createApi } from '@reduxjs/toolkit/query/react';

import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';

/** Dashboards the backend will mint an embed ticket for. */
export type EmbedDashboard = 'marketing';

export interface EmbedTicket {
  /**
   * Bearer credential for the dashboard's exchange endpoint. Single-use, and
   * valid for 60 seconds. Never persist, log, or put it in a URL of our own.
   */
  ticket: string;
  dashboard: EmbedDashboard;
  expiresAt: string;
}

/**
 * Ticket minting is a MUTATION on purpose.
 *
 * A ticket is single-use and expires in 60 seconds, so anything that could
 * replay or re-serve one — the query cache, `refetchOnFocus`,
 * `refetchOnReconnect`, a retry — would hand the iframe a ticket the backend
 * has already burned. A mutation gives exactly one round trip per call, and
 * the caller resets the cache entry as soon as it has read the value.
 */
export const embedApi = createApi({
  reducerPath: 'embedApi',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    createEmbedTicket: builder.mutation<EmbedTicket, EmbedDashboard>({
      query: (dashboard) => ({
        url: 'v1/embed/ticket',
        method: 'POST',
        body: { dashboard },
      }),
      transformResponse: (res: ApiResponse<EmbedTicket>) => res.data,
    }),
  }),
});

export const { useCreateEmbedTicketMutation } = embedApi;
